import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MapPin, Users, Trash2, Save, User, Search, Loader2, Upload, Camera, ExternalLink, Link2 } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import RichTextEditor from '@/components/RichTextEditor';
import { API } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  'Président(e)', 'Vice-Président(e)', 'Secrétaire', 'Secrétaire Adjoint(e)',
  'Trésorier(ère)', 'Trésorier(ère) Adjoint(e)',
  'Membre du CA', 'Suppléant(e)',
];

const UserSearchSelector = ({ onSelect, existingIds = [] }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/users/search-for-bureau`, { params: { q: query } });
        setResults(res.data.filter(u => !existingIds.includes(u.id)));
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, existingIds]);

  return (
    <div ref={ref} className="relative" data-testid="user-search-selector">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={query} onChange={e => { setQuery(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          placeholder="Rechercher un membre par nom..."
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] outline-none"
          data-testid="regional-member-search" />
        {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border border-gray-200 shadow-xl max-h-60 overflow-y-auto">
          {results.map(user => (
            <button key={user.id} onClick={() => { onSelect(user); setQuery(''); setOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#eaf6fc] transition-colors text-left"
              data-testid={`select-regional-user-${user.id}`}>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white text-xs font-bold overflow-hidden flex-shrink-0">
                {user.profile_image ? <img src={`${BACKEND_URL}${user.profile_image}`} alt="" className="w-full h-full object-cover" /> : `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">Dr {user.first_name} {user.last_name}</p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && query.length >= 2 && results.length === 0 && !searching && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border border-gray-200 shadow-xl p-4 text-center">
          <p className="text-sm text-gray-400">Aucun membre trouvé</p>
        </div>
      )}
    </div>
  );
};

const RegionalBureauPage = () => {
  const navigate = useNavigate();
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [members, setMembers] = useState([]);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const fetchRegions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/regions`);
      setRegions(res.data);
      if (!selectedSlug && res.data.length > 0) {
        setSelectedSlug(res.data[0].slug);
      }
    } catch { toast.error('Erreur chargement régions'); }
    finally { setLoading(false); }
  }, [selectedSlug]);

  useEffect(() => { fetchRegions(); }, []);

  const selectedRegion = regions.find(r => r.slug === selectedSlug);

  useEffect(() => {
    if (selectedRegion) {
      setMembers(selectedRegion.members?.map(m => ({ ...m })) || []);
      setDescription(selectedRegion.description || '');
    }
  }, [selectedSlug, regions]);

  const addMemberFromSearch = (user) => {
    setMembers(prev => [...prev, {
      user_id: user.id,
      name: `Dr ${user.first_name} ${user.last_name}`,
      role: ROLE_OPTIONS[0],
      photo_url: user.profile_image || '',
    }]);
  };

  const removeMember = (idx) => setMembers(prev => prev.filter((_, i) => i !== idx));
  const updateMember = (idx, field, value) => setMembers(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));

  const saveAll = async () => {
    if (!selectedRegion) return;
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/bureau/regional/${selectedSlug}/save`, { members }),
        axios.put(`${API}/bureau/regional/${selectedSlug}/update-info`, { description }),
      ]);
      toast.success(`Bureau ${selectedRegion.name} sauvegardé`);
      fetchRegions();
    } catch { toast.error('Erreur de sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSlug) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/bureau/regional/${selectedSlug}/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo mis à jour');
      fetchRegions();
    } catch { toast.error('Erreur upload logo'); }
    finally { setUploadingLogo(false); }
  };

  const getExistingIds = () => members.map(m => m.user_id).filter(Boolean);
  const totalMembers = regions.reduce((s, r) => s + (r.members?.length || 0), 0);
  const regionsWithBureau = regions.filter(r => r.members?.length > 0).length;
  const logoSrc = selectedRegion?.logo_url
    ? (selectedRegion.logo_url.startsWith('http') ? selectedRegion.logo_url : `${BACKEND_URL}${selectedRegion.logo_url}`)
    : null;

  if (loading) return <AdminLayout><div className="flex justify-center items-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#35AFE4]" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-6xl mx-auto" data-testid="regional-bureau-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center">
              <MapPin size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Bureaux Régionaux</h1>
              <p className="text-xs text-gray-500">{regions.length} régions — {totalMembers} membres — {regionsWithBureau} bureaux renseignés</p>
            </div>
          </div>
        </div>

        <div className="flex gap-6">
          {/* LEFT: Region selector */}
          <div className="w-56 flex-shrink-0">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden sticky top-4">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Régions</p>
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {regions.map(r => {
                  const isActive = r.slug === selectedSlug;
                  const hasMembers = r.members?.length > 0;
                  const rLogoSrc = r.logo_url ? (r.logo_url.startsWith('http') ? r.logo_url : `${BACKEND_URL}${r.logo_url}`) : null;
                  return (
                    <button key={r.slug} onClick={() => setSelectedSlug(r.slug)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all border-l-3 ${isActive ? 'bg-[#eaf6fc] border-l-[#35AFE4] border-l-[3px]' : 'hover:bg-gray-50 border-l-transparent border-l-[3px]'}`}
                      data-testid={`region-tab-${r.slug}`}>
                      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
                        {rLogoSrc ? (
                          <img src={rLogoSrc} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-[10px] font-bold text-gray-400">{r.name?.substring(0, 2).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-xs font-semibold truncate ${isActive ? 'text-[#1e3a5f]' : 'text-gray-700'}`}>{r.name}</p>
                        <p className="text-[10px] text-gray-400">{hasMembers ? `${r.members.length} membres` : 'Vide'}</p>
                      </div>
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${hasMembers ? 'bg-emerald-400' : 'bg-orange-300'}`} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT: Selected region detail */}
          <div className="flex-1 min-w-0">
            {selectedRegion ? (
              <div className="space-y-5">
                {/* Save button */}
                <div className="flex justify-end">
                  <button onClick={saveAll} disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#1e3a5f] text-white rounded-xl text-sm font-bold hover:bg-[#162d4a] disabled:opacity-50 transition-colors" data-testid="save-regional-bureau">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Sauvegarder
                  </button>
                </div>

                {/* Logo + Name */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="flex items-center gap-5">
                    {/* Logo upload */}
                    <div className="relative group cursor-pointer" onClick={() => logoInputRef.current?.click()} data-testid="region-logo-upload">
                      <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-gray-100 bg-gray-50 flex items-center justify-center">
                        {uploadingLogo ? (
                          <Loader2 size={20} className="animate-spin text-gray-400" />
                        ) : logoSrc ? (
                          <img src={logoSrc} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <MapPin size={24} className="text-gray-300" />
                        )}
                      </div>
                      <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Camera size={16} className="text-white" />
                      </div>
                      <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    </div>

                    <div>
                      <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="region-name">
                        {selectedRegion.name}
                      </h2>
                      <p className="text-xs text-gray-500">{selectedRegion.departments?.length || 0} départements</p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Présentation de la région</label>
                  <RichTextEditor
                    value={description}
                    onChange={setDescription}
                    placeholder="Texte de présentation de l'antenne régionale..."
                    height="120px"
                  />
                </div>

                {/* Members */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Membres du bureau ({members.length})
                    </p>
                  </div>

                  {/* Search selector */}
                  <div className="bg-[#f4fafe] rounded-xl border border-dashed border-[#35AFE4] p-4 mb-4">
                    <p className="text-xs text-gray-500 mb-2 font-medium">Ajouter un membre (recherche parmi les membres de l'app) :</p>
                    <UserSearchSelector
                      onSelect={addMemberFromSearch}
                      existingIds={getExistingIds()}
                    />
                  </div>

                  {/* Members list */}
                  <div className="space-y-2">
                    {members.map((m, i) => {
                      const linked = !!m.user_id;
                      const goToProfile = () => { if (linked) navigate(`/admin/members?user=${m.user_id}`); };
                      return (
                      <div key={`${m.user_id || i}`} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 group" data-testid={`regional-member-row-${i}`}>
                        <button onClick={goToProfile} disabled={!linked}
                          className={`w-10 h-10 rounded-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white overflow-hidden flex-shrink-0 ${linked ? 'cursor-pointer hover:ring-2 hover:ring-[#35AFE4]/50 transition-all' : 'cursor-default'}`}
                          title={linked ? 'Voir le profil' : 'Membre non lié'}
                          data-testid={`regional-avatar-${i}`}>
                          {m.photo_url ? (
                            <img src={m.photo_url.startsWith('http') ? m.photo_url : `${BACKEND_URL}${m.photo_url}`} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User size={16} />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          {linked ? (
                            <button onClick={goToProfile}
                              className="text-sm font-semibold text-gray-900 truncate hover:text-[#35AFE4] transition-colors flex items-center gap-1.5"
                              data-testid={`regional-name-${i}`}>
                              {m.name}
                              <ExternalLink size={11} className="opacity-50 group-hover:opacity-100 transition-opacity" />
                            </button>
                          ) : (
                            <p className="text-sm font-semibold text-gray-400 truncate">{m.name}</p>
                          )}
                          {linked ? (
                            <p className="text-[10px] text-emerald-600 flex items-center gap-1"><Link2 size={9} /> Lié au compte</p>
                          ) : (
                            <p className="text-[10px] text-orange-500">Non lié — supprimez et ré-ajoutez via la recherche</p>
                          )}
                        </div>
                        <input
                          list={`roles-region-${i}`}
                          value={m.role}
                          onChange={e => updateMember(i, 'role', e.target.value)}
                          placeholder="Fonction (saisie libre)"
                          className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white w-48 flex-shrink-0"
                          data-testid={`regional-member-role-${i}`}
                        />
                        <datalist id={`roles-region-${i}`}>
                          {ROLE_OPTIONS.map(r => <option key={r} value={r} />)}
                        </datalist>
                        <button onClick={() => removeMember(i)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                          data-testid={`remove-regional-member-${i}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                      );
                    })}
                  </div>
                  {members.length === 0 && (
                    <div className="text-center py-12 bg-gray-50 rounded-xl mt-2">
                      <User size={32} className="mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-400">Aucun membre du bureau. Utilisez la recherche ci-dessus.</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <div className="text-center">
                  <MapPin size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Sélectionnez une région dans la liste</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default RegionalBureauPage;
